import React, { useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import Modal from 'react-native-modal';
import { AppText } from '../AppText';
import FilledButton from '../FilledButton';
import { useEventAvailability } from './useEventAvailability';
import { compareByOpensAt, formatEventWindow, formatRetrievedAt, formatSlotTime } from './siteFormatting';
import Colors from '../../constants/Colors';
import { useTranslation } from '../../providers/TranslationProvider';
import { Slot, Window } from '../../types/Site';

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    backgroundColor: Colors.white,
    borderRadius: 8,
    maxHeight: '80%',
  },
  header: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: Colors.primaryColor,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  headerTitle: { color: Colors.white, fontSize: 20, fontWeight: '700' },
  headerSubtitle: { color: Colors.white, fontSize: 15, marginTop: 4 },
  headerCaption: { color: Colors.white, fontSize: 12, marginTop: 8, opacity: 0.8 },
  body: { paddingVertical: 12, paddingHorizontal: 20 },
  slotList: { flexShrink: 1 },
  centered: { justifyContent: 'center', alignItems: 'center', minHeight: 120 },
  errorText: { fontSize: 15, color: Colors.veryDarkGray, marginBottom: 12, textAlign: 'center' },
  noSlotsText: { fontSize: 15, color: Colors.veryDarkGray, textAlign: 'center' },
  slotRow: {
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: Colors.grayBorder,
    borderRadius: 8,
    marginBottom: 8,
  },
  slotRowSelected: {
    borderColor: Colors.primaryColor,
    backgroundColor: Colors.lightBackground,
  },
  slotRowFull: {
    borderColor: Colors.grayBorder,
    backgroundColor: Colors.lightGrayBackground,
  },
  slotText: { fontSize: 15, color: Colors.veryDarkGray },
  slotTextFull: { fontSize: 15, color: Colors.grayInfoText },
  fullLabel: { fontSize: 13, color: Colors.grayInfoText, marginTop: 2 },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
  },
  cancelText: { fontSize: 16, fontWeight: '500', color: Colors.veryDarkGray },
  confirmButton: { width: 120 },
});

function isSlotFull(slot: Slot): boolean {
  return slot.openSlots === 0;
}

function sortByOpensAt(slots: Slot[]): Slot[] {
  return [...slots].sort(compareByOpensAt);
}

export interface EventSlotPickerModalProps {
  occurrenceId: string;
  eventName: string;
  window: Window;
  timezone?: string;
  onClose: () => void;
}

export function EventSlotPickerModal({
  occurrenceId,
  eventName,
  window,
  timezone,
  onClose,
}: EventSlotPickerModalProps) {
  const { t } = useTranslation();
  const { availability, loading, error, refetch } = useEventAvailability(occurrenceId, window);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const slots = sortByOpensAt(Array.isArray(availability?.slots) ? availability.slots : []);

  return (
    <Modal isVisible onBackdropPress={onClose} onBackButtonPress={onClose}>
      <View style={styles.contentContainer}>
        <View style={styles.header}>
          <AppText style={styles.headerTitle}>{t('pick_visit_time')}</AppText>
          <AppText style={styles.headerSubtitle}>
            {eventName} · {formatEventWindow(window, timezone)}
          </AppText>
          {availability && (
            <AppText style={styles.headerCaption}>
              {t('site_details_availability_as_of', {
                time: formatRetrievedAt(availability.retrievedAt, timezone),
              })}
            </AppText>
          )}
        </View>
        {loading && (
          <View style={[styles.body, styles.centered]} testID="event-slot-picker-loading">
            <ActivityIndicator size="large" color={Colors.primaryColor} />
          </View>
        )}
        {!loading && error && (
          <View style={[styles.body, styles.centered]}>
            <AppText style={styles.errorText}>{t('site_details_availability_error')}</AppText>
            <FilledButton
              testID="event-slot-picker-retry"
              onPress={refetch}
              label={t('site_map_retry')}
              color={Colors.primaryColor}
              full={false}
              height={40}
            />
          </View>
        )}
        {!loading && !error && slots.length === 0 && (
          <View style={[styles.body, styles.centered]}>
            <AppText style={styles.noSlotsText}>{t('site_details_slot_picker_no_times')}</AppText>
          </View>
        )}
        {!loading && !error && slots.length > 0 && (
          <ScrollView style={[styles.body, styles.slotList]}>
            {slots.map((slot) => {
              const full = isSlotFull(slot);
              const selected = selectedSlotId === slot.sourceSlotId;
              return (
                <TouchableOpacity
                  key={slot.sourceSlotId}
                  testID={`event-slot-${slot.sourceSlotId}`}
                  disabled={full}
                  onPress={() => setSelectedSlotId(slot.sourceSlotId)}
                  style={[styles.slotRow, full && styles.slotRowFull, selected && styles.slotRowSelected]}
                  accessibilityState={{ disabled: full, selected }}
                >
                  <AppText style={full ? styles.slotTextFull : styles.slotText}>
                    {formatSlotTime(slot, timezone)}
                  </AppText>
                  {full && <AppText style={styles.fullLabel}>{t('site_details_slot_full')}</AppText>}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
        <View style={styles.buttonRow}>
          <TouchableOpacity onPress={onClose} testID="event-slot-picker-cancel">
            <AppText style={styles.cancelText}>{t('app_button_cancel')}</AppText>
          </TouchableOpacity>
          <View style={styles.confirmButton}>
            <FilledButton
              testID="event-slot-picker-confirm"
              onPress={() => {}}
              label={t('app_button_confirm')}
              color={Colors.primaryColor}
              full
              disabled
              height={44}
              radius={22}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}
